/**
 * The draft's image, held on this device in IndexedDB until the account exists: a File is too big
 * for localStorage. Stored as bytes plus name and type, which every IndexedDB can clone.
 */
const DB = 'express-draft';
const STORE = 'files';
const KEY = 'image';

// Where IndexedDB is off, as in some private windows, the image lives only as long as the page.
let pageOnly: File | null = null;

interface StoredImage {
  name: string;
  type: string;
  bytes: ArrayBuffer;
}

// FileReader rather than Blob.arrayBuffer: every browser has it, and so does the test DOM.
function bytesOf(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function run<T>(mode: IDBTransactionMode, act: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = act(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

/** The image the draft holds, or null. */
export async function readDraftImage(): Promise<File | null> {
  try {
    const stored = await run<StoredImage | undefined>('readonly', (store) => store.get(KEY));
    return stored ? new File([stored.bytes], stored.name, { type: stored.type }) : null;
  } catch {
    return pageOnly;
  }
}

/** Holds `file` as the draft's image, or drops the image when null. */
export async function writeDraftImage(file: File | null): Promise<void> {
  pageOnly = file;
  try {
    if (!file) {
      await run('readwrite', (store) => store.delete(KEY));
      return;
    }
    const stored: StoredImage = { name: file.name, type: file.type, bytes: await bytesOf(file) };
    await run('readwrite', (store) => store.put(stored, KEY));
  } catch {
    // Kept in pageOnly.
  }
}
